import React, { useState, useEffect, useRef } from 'react';
import { SHADOW, COLORS, RADIUS } from './theme';

function parseAddressComponents(components, formattedAddress) {
  const result = {
    address: '',
    city: '',
    state: '',
    zipCode: '',
    fullAddress: formattedAddress || ''
  };

  let streetNumber = '';
  let route = '';

  const list = components || [];

  list.forEach(function (component) {
    const types = component.types || [];
    const longText = component.longText || component.long_name || '';
    const shortText = component.shortText || component.short_name || '';

    if (types.indexOf('street_number') !== -1) {
      streetNumber = longText;
    }
    if (types.indexOf('route') !== -1) {
      route = longText;
    }
    if (types.indexOf('locality') !== -1) {
      result.city = longText;
    }
    if (!result.city && types.indexOf('sublocality') !== -1) {
      result.city = longText;
    }
    if (!result.city && types.indexOf('postal_town') !== -1) {
      result.city = longText;
    }
    if (types.indexOf('administrative_area_level_1') !== -1) {
      result.state = shortText;
    }
    if (types.indexOf('postal_code') !== -1) {
      result.zipCode = longText;
    }
  });

  if (streetNumber && route) {
    result.address = streetNumber + ' ' + route;
  } else if (route) {
    result.address = route;
  }

  if (!result.fullAddress) {
    const parts = [];
    if (result.address) parts.push(result.address);
    if (result.city) parts.push(result.city);
    if (result.state) parts.push(result.state);
    if (result.zipCode) parts.push(result.zipCode);
    result.fullAddress = parts.join(', ');
  }

  return result;
}

function AddressAutocomplete({ value, onChange, onSelect, placeholder, style, name, testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [placesStatus, setPlacesStatus] = useState('loading');
  const [errorText, setErrorText] = useState('');

  const placesLibRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextLookupRef = useRef(false);
  const onSelectRef = useRef(onSelect);

  useEffect(function () {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(function () {
    let cancelled = false;
    let timer = null;

    const tryLoad = async function () {
      if (cancelled) return;

      const maps = window.google && window.google.maps ? window.google.maps : null;

      if (maps && maps.places && maps.places.AutocompleteSuggestion) {
        placesLibRef.current = maps.places;
        setPlacesStatus('ready');
        return;
      }

      if (maps && maps.importLibrary) {
        try {
          const lib = await maps.importLibrary('places');
          if (cancelled) return;

          if (lib && lib.AutocompleteSuggestion) {
            placesLibRef.current = lib;
            setPlacesStatus('ready');
            return;
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Places library failed to load:', error);
            setPlacesStatus('error');
            setErrorText('Address suggestions unavailable. You can type the address manually.');
          }
          return;
        }
      }

      timer = setTimeout(tryLoad, 500);
    };

    tryLoad();

    return function () {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const getSessionToken = () => {
    const places = placesLibRef.current;
    if (!places || !places.AutocompleteSessionToken) return null;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const fetchSuggestions = async (text) => {
    const places = placesLibRef.current;
    if (!places || !places.AutocompleteSuggestion) return;

    try {
      // No includedRegionCodes restriction — this component is used app-wide (customers, work
      // orders, users, company address, projects, signup), and hardcoding it to 'us' meant a
      // company outside the US got zero autocomplete suggestions on every address field in the
      // app, with no setting anywhere to change it.
      const request = { input: text };

      const token = getSessionToken();
      if (token) {
        request.sessionToken = token;
      }

      const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      const list = response && response.suggestions ? response.suggestions : [];

      const cleaned = [];

      list.forEach(function (item) {
        const prediction = item.placePrediction;
        if (!prediction) return;

        let mainText = '';
        let secondaryText = '';

        if (prediction.mainText && prediction.mainText.toString) {
          mainText = prediction.mainText.toString();
        } else if (prediction.text && prediction.text.toString) {
          mainText = prediction.text.toString();
        }

        if (prediction.secondaryText && prediction.secondaryText.toString) {
          secondaryText = prediction.secondaryText.toString();
        }

        cleaned.push({
          key: prediction.placeId || mainText + secondaryText,
          mainText: mainText,
          secondaryText: secondaryText,
          prediction: prediction
        });
      });

      setSuggestions(cleaned);
      setShowDropdown(cleaned.length > 0);
      setErrorText('');
    } catch (error) {
      console.error('Address lookup failed:', error);
      setSuggestions([]);
      setShowDropdown(false);
      setErrorText('Address lookup failed. You can type the address manually.');
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;

    if (onChange) {
      onChange(text);
    }

    if (skipNextLookupRef.current) {
      skipNextLookupRef.current = false;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!text || text.length < 3 || placesStatus !== 'ready') {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(function () {
      fetchSuggestions(text);
    }, 300);
  };

  const handleSelectSuggestion = async (item) => {
    setShowDropdown(false);
    setSuggestions([]);
    // Deliberately NOT set. Real bug fixed here: this flag was raised before calling onSelect, but
    // every caller applies the chosen address through the controlled `value` prop, which does not
    // fire the input's onChange — so handleChange never ran for the selection and never consumed
    // the flag. It survived until the user's NEXT real keystroke, which then returned early and
    // showed no suggestions at all. The dropdown is already closed below, which is what this was
    // trying to achieve.

    try {
      const place = item.prediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

      const components = place.addressComponents || [];
      const formatted = place.formattedAddress || '';
      const parsed = parseAddressComponents(components, formatted);

      sessionTokenRef.current = null;

      if (onSelectRef.current) {
        onSelectRef.current(parsed);
      }
    } catch (error) {
      console.error('Could not load address details:', error);

      const fallbackText = item.secondaryText ? item.mainText + ', ' + item.secondaryText : item.mainText;

      if (onChange) {
        onChange(fallbackText);
      }

      // Places details lookup failed, but the user did pick a specific suggestion — call onSelect
      // with whatever can be salvaged (the full suggestion text) rather than leaving the caller's
      // structured city/state/zip fields silently blank with no signal anything happened. The
      // caller's form still ends up with the full address string, just not split into parts.
      if (onSelectRef.current) {
        onSelectRef.current({ address: fallbackText, city: '', state: '', zipCode: '', fullAddress: fallbackText });
      }

      setErrorText('Could not split this address into city/state/zip — please fill those in manually.');
    }
  };

  const handleBlur = () => {
    setTimeout(function () {
      setShowDropdown(false);
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        name={name}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
        style={style}
      />

      {/* zIndex:10 matches every other form-embedded suggestion/autocomplete dropdown in
          WorkOrderForm.js/WorkOrderDetail.js (this component is always embedded in one of
          those forms) — was a large, unexplained 3000 that didn't match any of them. */}
      {showDropdown && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: '0', right: '0', backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`, borderTop: 'none', borderRadius: '0 0 ' + RADIUS.md + ' ' + RADIUS.md, maxHeight: '220px', overflowY: 'auto', zIndex: 10, boxShadow: SHADOW.raised }}>
          {suggestions.map(function (item) {
            return (
              <div
                key={item.key}
                onMouseDown={() => handleSelectSuggestion(item)}
                style={{ padding: '12px 10px', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}`, transition: 'all 0.2s ease', backgroundColor: COLORS.rowHover }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.primaryBg}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = COLORS.rowHover}
              >
                <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '600', color: COLORS.text }}>{item.mainText}</p>
                {item.secondaryText && (
                  <p style={{ margin: '0', fontSize: '11px', color: COLORS.textFaint }}>{item.secondaryText}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {placesStatus === 'loading' && (
        <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: COLORS.textFaint }}>Address suggestions loading...</p>
      )}

      {errorText && (
        <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: COLORS.danger }}>{errorText}</p>
      )}
    </div>
  );
}

export default AddressAutocomplete;